import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import useDesktopScope from './useDesktopScope';
import useRendererOperation from './useRendererOperation';
import { captureAssetTransport } from '@/api/assetTransport';
import { assetActionAllowed, assetMutationOutcome, validAssetObservation } from '@/utils/assetObservation';

export function useOfflineAssetControls({ kind, fetchStatus, install, rollback, labels, runtime }) {
  const scope = useDesktopScope();
  const latest = useRef(null); latest.current = { fetchStatus, install, rollback, labels, runtime };
  const owner = useMemo(() => ({ scope, kind, active: false, sequence: 0, phase: 'loading', value: null, pending: new Set() }), [scope, kind]);
  const [view, setView] = useState(null);
  const operation = useRendererOperation(scope.bridge, `asset:${kind}:${scope.bridge?.localBackendUrl}`);
  const current = useCallback(() => owner.active && scope.current() && scope.admin, [owner, scope]);
  const publish = useCallback(() => {
    if (current()) setView({ owner, status: owner.value, phase: owner.phase });
  }, [current, owner]);
  const refresh = useCallback(async () => {
    if (!current()) return;
    const sequence = ++owner.sequence;
    owner.phase = 'loading'; publish();
    let timer; let retire;
    const expiry = new Promise((_, reject) => { retire = () => reject(new Error('Read unavailable')); timer = setTimeout(retire, 30000); });
    owner.pending.add(retire);
    try {
      const context = captureAssetTransport(scope);
      const result = await Promise.race([Promise.resolve().then(() => {
        if (!current()) throw new Error('Retired');
        return latest.current.fetchStatus(context);
      }), expiry]);
      if (!validAssetObservation(kind, result)) throw new Error('Invalid observation');
      if (current() && sequence === owner.sequence) { owner.value = result; owner.phase = 'ready'; publish(); }
    } catch {
      if (current() && sequence === owner.sequence) { owner.phase = 'unavailable'; publish(); }
    } finally { clearTimeout(timer); owner.pending.delete(retire); }
  }, [current, owner, scope, kind, publish]);
  useEffect(() => {
    owner.active = true; refresh();
    return () => { owner.active = false; for (const retire of owner.pending) retire(); };
  }, [owner, refresh]);
  // A replacement opening may have read the old package while another opening's
  // mutation was pending. Settlement invalidates that read, not its feedback owner.
  const needsSettlementRead = !operation.claim && owner.observedClaim?.phase === 'pending'
    && owner.observedClaim !== owner.dispatched;
  useEffect(() => {
    const previous = owner.observedClaim;
    owner.observedClaim = operation.claim;
    if (!operation.claim && previous?.phase === 'pending' && previous !== owner.dispatched) refresh();
  }, [operation.claim, owner, refresh]);
  const eligible = action => {
    const observed = latest.current.runtime;
    return current() && !needsSettlementRead && owner.phase === 'ready' && observed?.current() && observed.phase === 'ready'
      && observed.status?.state === 'ready' && (kind !== 'routing' || scope.cloud)
      && assetActionAllowed(kind, action, owner.value);
  };
  const run = async action => {
    if (!eligible(action)) return;
    const token = operation.acquire(action);
    if (!token) return;
    owner.dispatched = token;
    let outcome = 'unknown';
    try {
      const context = captureAssetTransport(scope);
      if (action === 'rollback' && !window.confirm(latest.current.labels.rollbackConfirm)) { outcome = 'cancelled'; return; }
      if (!eligible(action)) { outcome = 'cancelled'; return; }
      const result = await latest.current[action](context);
      outcome = assetMutationOutcome(kind, action, result);
      if (!current()) return;
      if (outcome === 'complete') {
        toast.success(action === 'install' ? latest.current.labels.installed : latest.current.labels.restored);
        await refresh();
      } else if (outcome === 'refused') {
        toast.error(result.error || 'The asset request did not start.');
      }
    } catch (error) {
      if (error?.assetNotDispatched) outcome = 'cancelled';
      // Transport/server failure is not evidence of rollback. Retain the claim.
    } finally {
      operation.settle(token, ['unknown', 'handoff'].includes(outcome) ? outcome : null);
    }
  };
  const visible = view?.owner === owner && scope.current() ? view : null;
  return { status: visible?.status || null, phase: visible?.phase || 'loading',
    loading: needsSettlementRead || !visible || visible.phase === 'loading', refresh,
    installing: operation.claim?.phase === 'pending' && operation.claim.kind === 'install',
    rollingBack: operation.claim?.phase === 'pending' && operation.claim.kind === 'rollback',
    busy: needsSettlementRead || !!operation.claim, outcome: operation.claim?.phase,
    onInstall: () => run('install'), onRollback: () => run('rollback') };
}
export default useOfflineAssetControls;
